import {useCallback, useEffect, useState} from 'react';

import {CompaniesSelectorTable} from '../../layout/company-popup-v2/companies-table/companies-selector-table';
import {CompanySelectHeader} from '../../layout/company-popup-v2/company-select-header';
import {CompanySelectModalFooter} from '../../layout/company-popup-v2/company-select-modal-footer';
import {
    PostSelectorCompanyType,
    UseCompaniesSelectorHookType,
} from '../../service/feature-companies/feature-companies-type';
import {useRefreshId} from '../../util/hook';

import {PublicationWizardStep} from './publication-wizard-step';

type PropsType = {
    companiesSelector: UseCompaniesSelectorHookType<PostSelectorCompanyType>;
    onPreviousStep: VoidFunction;
    onStepFinished: VoidFunction;
    onStepActivated: (width: number) => void;
    width: number;
    disabled?: boolean;
};

// Shared "select companies" wizard step, backed by the posts companies-selector. Manual selection only.
export function PublicationSelectCompaniesStep(props: PropsType): JSX.Element {
    const {companiesSelector, onPreviousStep, onStepFinished, onStepActivated, width, disabled} = props;

    const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);

    const {
        selectorId,
        isInProgress,
        companies,
        selectCompany,
        deselectCompany,
        loadMoreCompanies,
        selectedCount,
        deselectAll,
        companiesSelectorFilter,
        clearFilter,
    } = companiesSelector;

    const {refresh: refreshTableScrollTrigger, refreshId: tableScrollTrigger} = useRefreshId();

    useEffect(() => {
        onStepActivated(width);
    }, [onStepActivated, width]);

    useEffect(() => {
        refreshTableScrollTrigger();
    }, [companiesSelectorFilter, refreshTableScrollTrigger]);

    useEffect(() => {
        return () => clearFilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelectFiltered = useCallback((brandId: number) => setSelectedBrandId(brandId), []);

    return (
        <PublicationWizardStep
            footer={
                <CompanySelectModalFooter
                    disabled={disabled}
                    hideSubmitButton
                    key="footer"
                    onClear={deselectAll}
                    selectedCount={selectedCount}
                    selectorId={selectorId}
                />
            }
            leftFooterButtonConfig={{
                langKeyType: 'BUTTON__BACK',
                onClick: onPreviousStep,
            }}
            rightFooterButtonConfig={{
                disabled: selectedCount === 0,
                langKeyType: 'BUTTON__NEXT',
                onClick: onStepFinished,
            }}
        >
            <CompanySelectHeader<PostSelectorCompanyType>
                beforeSelectFromMainFilter={refreshTableScrollTrigger}
                companiesSelector={companiesSelector}
                disabled={disabled}
                onSelectFiltered={handleSelectFiltered}
                selectedBrandId={selectedBrandId}
            />

            <CompaniesSelectorTable<PostSelectorCompanyType>
                companies={companies}
                disabled={disabled}
                isInProgress={isInProgress}
                loadMoreCompanies={loadMoreCompanies}
                onDeselectCompany={deselectCompany}
                onSelectCompany={selectCompany}
                scrollToTopDependency={tableScrollTrigger}
                selectedBrandId={selectedBrandId}
            />
        </PublicationWizardStep>
    );
}
